import React, { useCallback, useEffect, useRef, useState } from 'react';
import AgentChatMessages from './AgentChatMessages';
import AgentChatInput from './AgentChatInput';
import ProgressPanel from './ProgressPanel';
import ResponseLetter from './ResponseLetter';
import { streamAgentMessage, PreStreamError } from './useAgentStream';
import { AgentChatMessage, AgentChatProps, AgentChatStreamEvent, AgentSave, ProgressItem, ReviewLetterState, StoredMessage } from './types';
import './AgentChat.css';

// Flatten one stored Anthropic content-block message into a render view-model. Tool_use /
// tool_result blocks carry no display text, so they flatten to '' and are skipped on load.
function blocksToMessage(stored: StoredMessage): AgentChatMessage | null {
  if (!stored || (stored.role !== 'user' && stored.role !== 'assistant')) return null;
  const text = (stored.content || [])
    .filter((b) => b && b.type === 'text' && typeof b.text === 'string')
    .map((b) => b.text as string)
    .join('\n')
    .trim();
  if (!text) return null; // pure tool exchange — nothing to show
  return { role: stored.role, text };
}

// Human-friendly label for the activity chip shown while a tool runs.
function activityLabel(name: string, qn: string | null): string {
  switch (name) {
    case 'save_answer':
      return qn ? `Saving ${qn}…` : 'Saving…';
    case 'read_answer':
      return qn ? `Reading ${qn}…` : 'Reading current draft…';
    case 'get_question_details':
      return qn ? `Looking up ${qn}…` : 'Looking up the requirements…';
    case 'check_progress':
      return 'Checking progress…';
    case 'record_response':
      return 'Updating the response letter…';
    default:
      return 'Working…';
  }
}

// Kickoff message sent automatically once the user pastes their IACUC review comments.
const REVIEW_KICKOFF = 'Please triage these review comments and tell me which ones you can help me revise here versus which I need to handle in InfoEd.';

const AgentChat: React.FC<AgentChatProps> = ({ protocol, apiBaseUrl, getAuthHeaders, onMainMenu, onOpenSummary, onProtocolChanged, sessionType = 'drafting' }) => {
  const isReview = sessionType === 'review';
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<ProgressItem[] | null>(null);
  const [streamingText, setStreamingText] = useState('');
  const [activity, setActivity] = useState<string | null>(null);

  // Review mode only: the pasted-comments gate. `needsComments` shows the paste box until a
  // review document is attached; `commentsInput` / `submittingComments` drive that form.
  const [needsComments, setNeedsComments] = useState(false);
  const [commentsInput, setCommentsInput] = useState('');
  const [submittingComments, setSubmittingComments] = useState(false);
  // Review mode only: the point-by-point letter, assembled server-side from the agent's
  // record_response entries. Refetched after every turn so entries appear as they're made.
  const [letter, setLetter] = useState<ReviewLetterState | null>(null);

  // Per-turn streaming accumulators (refs avoid stale closures inside the event handler).
  const abortRef = useRef<AbortController | null>(null);
  const streamTextRef = useRef('');
  const savesRef = useRef<AgentSave[]>([]);
  const sawToolRef = useRef(false); // a tool ran since the last text → insert a paragraph break

  const base = `${apiBaseUrl}/api/agent-chat/${protocol.id}`;
  // Query suffix that pins every session-scoped call to the right session type.
  const typeQuery = isReview ? '?type=review' : '';

  const refreshProgress = useCallback(() => {
    fetch(`${base}/progress`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.progress) setProgress(d.progress);
      })
      .catch(() => {
        /* non-fatal */
      });
  }, [base, getAuthHeaders]);

  const refreshLetter = useCallback(() => {
    if (!isReview) return;
    fetch(`${base}/review-document`, { headers: getAuthHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (d && d.document) setLetter({ roundNumber: d.document.roundNumber, responses: d.document.responses || [] });
      })
      .catch(() => {
        /* non-fatal */
      });
  }, [isReview, base, getAuthHeaders]);

  // Load the active session + progress on mount / protocol change. In review mode, first
  // check whether comments are attached: if not, show the paste box instead of the session.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setNeedsComments(false);
    setLetter(null);

    const loadReviewDoc = isReview
      ? fetch(`${base}/review-document`, { headers: getAuthHeaders() }).then((r) => (r.ok ? r.json() : Promise.reject()))
      : Promise.resolve(null);

    Promise.all([
      loadReviewDoc,
      fetch(`${base}/session${typeQuery}`, { headers: getAuthHeaders() }).then((r) => (r.ok ? r.json() : Promise.reject())),
      fetch(`${base}/progress`, { headers: getAuthHeaders() }).then((r) => (r.ok ? r.json() : Promise.reject()))
    ])
      .then(([reviewData, sessionData, progressData]) => {
        if (cancelled) return;
        const stored: StoredMessage[] = sessionData?.session?.messages || [];
        setMessages(stored.map(blocksToMessage).filter((m): m is AgentChatMessage => m !== null));
        setProgress(progressData?.progress || []);
        // Review + no attached comments → gate on the paste box.
        if (isReview && !(reviewData && reviewData.document)) setNeedsComments(true);
        if (isReview && reviewData && reviewData.document) {
          setLetter({ roundNumber: reviewData.document.roundNumber, responses: reviewData.document.responses || [] });
        }
      })
      .catch(() => {
        if (!cancelled) setError('Could not load this conversation. You can still start chatting.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      if (abortRef.current) abortRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocol.id, isReview]);

  const sendMessage = useCallback(async (rawText: string) => {
    const text = rawText.trim();
    if (!text || isSending) return;

    setError(null);
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', text }]); // optimistic
    setIsSending(true);
    setStreamingText('');
    setActivity(null);
    streamTextRef.current = '';
    savesRef.current = [];
    sawToolRef.current = false;

    const controller = new AbortController();
    abortRef.current = controller;

    let finalized = false;
    // Commit the streamed turn into the message list exactly once. Prefer the streamed text
    // (what the user watched) over turn_done's `reply` (only the final text block).
    const finalize = (reply?: string, savesList?: AgentSave[]) => {
      if (finalized) return;
      finalized = true;
      const bodyText = streamTextRef.current.trim() || (reply || '').trim();
      const saves = savesList ?? savesRef.current;
      if (bodyText || saves.length > 0) {
        setMessages((prev) => [...prev, { role: 'assistant', text: bodyText, saves }]);
      }
      if (saves.length > 0 && onProtocolChanged) onProtocolChanged();
      setStreamingText('');
      setActivity(null);
      setIsSending(false);
    };

    const onEvent = (ev: AgentChatStreamEvent) => {
      switch (ev.type) {
        case 'text_delta': {
          if (sawToolRef.current && streamTextRef.current) streamTextRef.current += '\n\n';
          sawToolRef.current = false;
          streamTextRef.current += ev.text;
          setStreamingText(streamTextRef.current);
          break;
        }
        case 'tool_start': {
          setActivity(activityLabel(ev.name, ev.question_number));
          sawToolRef.current = true;
          break;
        }
        case 'tool_done': {
          if (ev.saved && ev.question_number) {
            savesRef.current = [...savesRef.current, { question_number: ev.question_number, version: ev.version ?? 0 }];
          }
          setActivity(null);
          break;
        }
        case 'turn_done': {
          if (ev.progress) setProgress(ev.progress);
          finalize(ev.reply, ev.saves);
          refreshLetter();
          break;
        }
        case 'error': {
          setError(ev.error || 'Something went wrong. Please try again.');
          finalize(); // keep any partial text the user already saw
          break;
        }
        default:
          break; // 'open' and any future events
      }
    };

    try {
      await streamAgentMessage({
        url: `${base}/message?stream=1${isReview ? '&type=review' : ''}`,
        headers: getAuthHeaders(),
        message: text,
        signal: controller.signal,
        onEvent
      });
      finalize(); // stream ended cleanly without a turn_done (defensive)
    } catch (err) {
      if (controller.signal.aborted) {
        // User clicked Stop. The server still finishes the in-flight tool calls and persists
        // completed saves + the transcript, so refresh progress to reflect them.
        finalize();
        setError('Stopped. Anything the agent saved before you stopped is saved.');
        refreshProgress();
        refreshLetter();
      } else if (err instanceof PreStreamError) {
        // Failed before the stream opened (e.g. another turn in progress, budget) — nothing
        // streamed, so roll the optimistic bubble back and let the user retry.
        setMessages((prev) => prev.slice(0, -1));
        setInput(text);
        setError(err.message);
      } else {
        // Connection dropped mid-stream — keep whatever streamed.
        finalize();
        setError('The connection dropped. Reload to confirm what was saved.');
      }
    } finally {
      abortRef.current = null;
      setIsSending(false);
      setStreamingText('');
      setActivity(null);
    }
  }, [isSending, base, getAuthHeaders, onProtocolChanged, refreshProgress, refreshLetter, isReview]);

  const handleSend = useCallback(() => sendMessage(input), [sendMessage, input]);

  // Review mode: submit the pasted IACUC comments, then auto-kick off the triage turn.
  const handleSubmitComments = useCallback(async () => {
    const comments = commentsInput.trim();
    if (!comments || submittingComments) return;
    setSubmittingComments(true);
    setError(null);
    try {
      const res = await fetch(`${base}/review-document`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ comments })
      });
      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error((data && data.error) || 'Could not save the comments.');
      }
      const data = await res.json().catch(() => null);
      setLetter({ roundNumber: (data && data.document && data.document.roundNumber) || 1, responses: [] });
      setNeedsComments(false);
      setCommentsInput('');
      sendMessage(REVIEW_KICKOFF); // let the agent triage immediately
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the comments. Please try again.');
    } finally {
      setSubmittingComments(false);
    }
  }, [commentsInput, submittingComments, base, getAuthHeaders, sendMessage]);

  const handleStop = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const handleReset = useCallback(async () => {
    if (isSending) return;
    const prompt = isReview
      ? 'Start a new review round? The current review conversation will be archived. Your saved drafts stay in the protocol, and you can paste a new set of comments.'
      : 'Start a new chat? The current conversation will be archived. Your saved drafts stay in the protocol.';
    if (!window.confirm(prompt)) return;
    setError(null);
    try {
      const res = await fetch(`${base}/reset${typeQuery}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }
      });
      if (!res.ok) throw new Error('reset failed');
      setMessages([]);
      if (isReview) {
        setNeedsComments(true); // back to the paste box for the next round
        setLetter(null);
      }
    } catch {
      setError('Could not start a new chat. Please try again.');
    }
  }, [isSending, base, getAuthHeaders, isReview, typeQuery]);

  return (
    <div className="agent-chat">
      <div className="agent-chat-header">
        <div className="agent-chat-title">
          <h2>{isReview ? 'Review Response' : 'Agent Mode'}</h2>
          <span className="agent-chat-protocol">{protocol.title}</span>
        </div>
        <div className="agent-chat-actions">
          <button className="agent-chat-btn-secondary" onClick={handleReset} disabled={isSending}>
            {isReview ? 'New Review Round' : 'New Chat'}
          </button>
          <button className="agent-chat-btn-secondary" onClick={onMainMenu}>
            Main Menu
          </button>
        </div>
      </div>

      <div className="agent-chat-disclaimer">
        {isReview ? (
          <>
            Paste the comments your IACUC returned. The agent sorts them into ones it can help you
            revise here and ones you handle in InfoEd, revises the affected narrative sections, and
            helps draft your response-to-reviewers letter. Verify everything against your committee's
            requirements.
          </>
        ) : (
          <>
            AI suggestions are a starting point — verify everything against your IACUC's requirements.
            The agent saves a few narrative sections directly; the rest it drafts for you to paste, and
            yes/no and numeric fields are entered in InfoEd.
          </>
        )}
      </div>

      <div className="agent-chat-layout">
        <div className="agent-chat-main">
          {isLoading ? (
            <div className="agent-chat-loading">Loading conversation…</div>
          ) : isReview && needsComments ? (
            <div className="agent-chat-review-intake">
              <h3>Paste the IACUC review comments</h3>
              <p>
                Copy the comments the committee returned (all of them) and paste them below. The agent
                will triage them and get started on your revisions and response letter.
              </p>
              <textarea
                className="agent-chat-review-textarea"
                value={commentsInput}
                onChange={(e) => setCommentsInput(e.target.value)}
                placeholder="Paste the full set of IACUC review comments here…"
                rows={12}
                disabled={submittingComments}
              />
              {error && <div className="agent-chat-error">{error}</div>}
              <button
                className="agent-chat-btn-primary"
                onClick={handleSubmitComments}
                disabled={submittingComments || !commentsInput.trim()}
              >
                {submittingComments ? 'Starting…' : 'Start review response'}
              </button>
            </div>
          ) : (
            <>
              <AgentChatMessages
                messages={messages}
                isSending={isSending}
                streamingText={streamingText}
                activity={activity}
              />

              {error && <div className="agent-chat-error">{error}</div>}

              <AgentChatInput
                value={input}
                onChange={setInput}
                onSend={handleSend}
                onStop={handleStop}
                isSending={isSending}
                disabled={isLoading || isSending}
              />
            </>
          )}
        </div>

        <ProgressPanel progress={progress} onOpenSummary={onOpenSummary}>
          {isReview && letter && (
            <ResponseLetter entries={letter.responses} roundNumber={letter.roundNumber} />
          )}
        </ProgressPanel>
      </div>
    </div>
  );
};

export default AgentChat;
